"use strict";
import { createContext, useContext, useMemo, useState } from "react";
import { UniverseChainId } from "features/chains/types";
import { useEvent } from "utilities/src/react/hooks";
export const UniswapContext = createContext(null);
export function UniswapProvider({
  children,
  account,
  connector,
  navigateToBuyOrReceiveWithEmptyWallet,
  navigateToFiatOnRamp,
  navigateToSwapFlow,
  navigateToSendFlow,
  navigateToReceive,
  navigateToTokenDetails,
  navigateToPoolDetails,
  navigateToExternalProfile,
  navigateToNftCollection,
  handleShareToken,
  onSwapChainsChanged,
  signer,
  useProviderHook,
  onConnectWallet,
  getGeneratePermitAsTransaction,
  getIsUniswapXSupported,
  handleOnPressUniswapXUnsupported,
  getCanBatchTransactions
}) {
  const [swapInputChainId, setSwapInputChainId] = useState(UniverseChainId.Mainnet);
  const [swapOutputChainId, setSwapOutputChainId] = useState(UniverseChainId.Mainnet);
  const [isSwapTokenSelectorOpen, setIsSwapTokenSelectorOpen] = useState(false);
  const value = useMemo(
    () => ({
      account,
      connector,
      navigateToBuyOrReceiveWithEmptyWallet,
      navigateToFiatOnRamp,
      navigateToSwapFlow,
      navigateToSendFlow,
      navigateToReceive,
      navigateToTokenDetails,
      navigateToPoolDetails,
      navigateToExternalProfile,
      navigateToNftCollection,
      handleShareToken,
      onSwapChainsChanged: ({
        chainId,
        prevChainId,
        outputChainId
      }) => {
        onSwapChainsChanged({ chainId, prevChainId, outputChainId });
        setSwapInputChainId(chainId);
        if (outputChainId !== void 0) {
          setSwapOutputChainId(outputChainId);
        }
      },
      signer,
      useProviderHook,
      onConnectWallet,
      swapInputChainId,
      swapOutputChainId,
      setSwapOutputChainId,
      isSwapTokenSelectorOpen,
      setIsSwapTokenSelectorOpen: (open) => setIsSwapTokenSelectorOpen(open),
      getGeneratePermitAsTransaction,
      getIsUniswapXSupported,
      handleOnPressUniswapXUnsupported,
      getCanBatchTransactions
    }),
    [
      account,
      connector,
      navigateToBuyOrReceiveWithEmptyWallet,
      navigateToFiatOnRamp,
      navigateToSwapFlow,
      navigateToSendFlow,
      navigateToReceive,
      navigateToTokenDetails,
      navigateToPoolDetails,
      navigateToExternalProfile,
      navigateToNftCollection,
      handleShareToken,
      signer,
      useProviderHook,
      onConnectWallet,
      swapInputChainId,
      swapOutputChainId,
      isSwapTokenSelectorOpen,
      setIsSwapTokenSelectorOpen,
      getGeneratePermitAsTransaction,
      onSwapChainsChanged,
      getIsUniswapXSupported,
      handleOnPressUniswapXUnsupported,
      getCanBatchTransactions
    ]
  );
  return <UniswapContext.Provider value={value}>{children}</UniswapContext.Provider>;
}
export function useUniswapContext() {
  const context = useContext(UniswapContext);
  if (!context) {
    throw new Error("useUniswapContext must be used within a UniswapProvider");
  }
  return context;
}
export function useUniswapContextSelector(selector) {
  const stableSelector = useEvent(selector);
  const context = useContext(UniswapContext);
  return context ? stableSelector(context) : void 0;
}
export function useAccountMeta() {
  return useUniswapContext().account;
}
export function useConnector() {
  return useUniswapContext().connector;
}
export function useProvider(chainId) {
  return useUniswapContext().useProviderHook(chainId);
}
export function useSigner() {
  return useUniswapContext().signer;
}
